/**
 * The hymnal index — a browse console, not a list.
 *
 * WHAT CHANGED AND WHY (2026-08-09): this page used to render a search box, four
 * stat tiles, eight meter chips, and then ALL 720 FIRST LINES bucketed A–Z. 266KB
 * of HTML with no filters, answering "is hymn X in here" — the question somebody
 * already holding the book has — while the question people arrive with is "what
 * can we sing in Advent".
 *
 * The old comment defended the A–Z dump on the grounds that the browse list is
 * what a crawler indexes and must not depend on a query string. That argument is
 * correct and is PRESERVED, not discarded: the full list still exists, complete
 * and crawlable and needing no query string, at `/hymns` — and it is now reachable
 * from 32 section pages instead of hanging off this one page alone.
 */
import { Link } from 'react-router-dom'

import { HYMNAL_1982, pages } from '@/data/hymnal'
import { PageHead } from '@/components/PageHead'
import { HymnBrowser } from '@/components/HymnBrowser'

const hymnalPath = (rest: string) => `/hymnal/${HYMNAL_1982.id}${rest}`

const INDEXES = [
  { href: '/hymns', label: 'All hymns A–Z', count: (n: typeof pages) => n.hymns.length },
  { href: '/tunes', label: 'Tunes', count: (n: typeof pages) => n.tunes.length },
  { href: '/people', label: 'People', count: (n: typeof pages) => n.people.length },
  { href: '/meters', label: 'Metres', count: (n: typeof pages) => n.meters.length },
]

export default function HymnalHomePage() {
  return (
    <main className="mx-auto max-w-4xl px-5 py-12">
      <PageHead
        title={`${HYMNAL_1982.label} — a reference index`}
        description={`All ${pages.hymns.length} hymns of ${HYMNAL_1982.label}, searchable by first line, number, tune and person, and filterable by season, metre and key.`}
        path={hymnalPath('')}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${HYMNAL_1982.label} — a reference index`,
          isPartOf: { '@type': 'WebSite', name: HYMNAL_1982.label },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: pages.sections.length,
            itemListElement: pages.sections.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: s.label,
              url: `https://fuguelab.com${hymnalPath(`/section/${s.slug}`)}`,
            })),
          },
        }}
      />

      <header className="mb-8">
        <h1 className="mb-3 font-display text-4xl text-foreground">{HYMNAL_1982.label}</h1>
        <p className="max-w-2xl text-muted-foreground">
          A reference index to all {pages.hymns.length} hymns — first lines, tunes, metres, composers, authors and
          translators, cross-linked so you can move from a text to its tune to everything else in the same metre.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Metadata only. This index carries no hymn texts and no music.
        </p>
      </header>

      <HymnBrowser />

      <section className="mt-12 border-t border-border pt-6">
        <h2 className="mb-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Complete indexes</h2>
        <ul className="flex flex-wrap gap-2">
          {INDEXES.map((i) => (
            <li key={i.href}>
              <Link
                to={hymnalPath(i.href)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded border border-border px-3 text-sm text-muted-foreground hover:border-border-strong hover:text-foreground"
              >
                {i.label}
                <span className="text-xs tabular-nums opacity-70">{i.count(pages)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  )
}
